import React from "react";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Bot, Globe, Terminal, Database, Calendar, Mic, GitPullRequest,
  Settings, Zap, FileCode, Mail, Folder, Code2, MessageSquare,
  FileText, DollarSign, Sparkles, Clock, Network, Bug, Eye,
  Webhook, Activity, Layout,
} from "lucide-react";

const Features = () => {
  const featureCategories = [
    {
      id: "ai",
      name: "AI & Chat",
      icon: <Bot className="h-5 w-5" />,
      features: [
        { icon: <MessageSquare className="h-6 w-6" />, title: "Rich Chat Interface", description: "Full Markdown support with code highlighting, tables, lists, and inline images" },
        { icon: <Bot className="h-6 w-6" />, title: "Multi-Provider AI", description: "OpenAI, OpenRouter, Google Gemini, and Local LLMs (Ollama-compatible)" },
        { icon: <Settings className="h-6 w-6" />, title: "GPT-5 Controls", description: "Fine-tune reasoning_effort and verbosity with automatic controls for GPT-5 models" },
        { icon: <FileText className="h-6 w-6" />, title: "Session Categories", description: "Color-coded categories with drag & drop organization and auto-naming" },
        { icon: <Code2 className="h-6 w-6" />, title: "Slash Commands", description: "Quick prompt templates via / autocomplete (e.g., /bug-report)" },
        { icon: <Sparkles className="h-6 w-6" />, title: "Prompt Enhancement", description: "AI-powered prompt improvement with customizable guidelines" },
        { icon: <DollarSign className="h-6 w-6" />, title: "Token & Cost Tracking", description: "Real-time monitoring with cost pills, budget limits, and CSV export" },
        { icon: <Layout className="h-6 w-6" />, title: "Context Meter", description: "Visualizes token usage and context limits in real-time" },
      ],
    },
    {
      id: "automation",
      name: "Automation",
      icon: <Zap className="h-5 w-5" />,
      features: [
        { icon: <Globe className="h-6 w-6" />, title: "Browser Automation", description: "Real Chrome/Chromium control with Playwright and accessibility tree navigation" },
        { icon: <Bug className="h-6 w-6" />, title: "Bug Reproduction Assistant", description: "Session recording with screenshots, network logs, and AI-generated reports" },
        { icon: <Webhook className="h-6 w-6" />, title: "Hooks System", description: "Event-driven automation with 11 hook events and command/script/AI execution" },
        { icon: <Activity className="h-6 w-6" />, title: "Recall Activity Tracking", description: "Automatic screenshot capture with AI-powered analysis and natural language search" },
        { icon: <Calendar className="h-6 w-6" />, title: "Scheduled Tasks", description: "Natural language scheduling for one-time and recurring tasks" },
        { icon: <GitPullRequest className="h-6 w-6" />, title: "PR Review Automation", description: "AI-powered code review with inline comments and diff viewer" },
        { icon: <Mic className="h-6 w-6" />, title: "System Dictation", description: "Global hotkey dictation with AI cleanup and multi-language support" },
        { icon: <Network className="h-6 w-6" />, title: "Dependency Mapping", description: "Interactive visualizations with impact analysis and circular dependency detection" },
      ],
    },
    {
      id: "tools",
      name: "Tool Ecosystem",
      icon: <Terminal className="h-5 w-5" />,
      features: [
        { icon: <FileText className="h-6 w-6" />, title: "Notes Tools (12)", description: "Create, search, and manage notes with categories and tags" },
        { icon: <Code2 className="h-6 w-6" />, title: "Dependency Map (10)", description: "Analyze TypeScript, JavaScript, C# codebases with interactive views" },
        { icon: <Eye className="h-6 w-6" />, title: "Recall Query Tools (4)", description: "Search desktop activity history with AI-powered queries" },
        { icon: <Terminal className="h-6 w-6" />, title: "Desktop Commander", description: "Terminal automation, filesystem operations, and code editing" },
        { icon: <Database className="h-6 w-6" />, title: "SQL Toolbox", description: "Connect to PostgreSQL, MySQL, SQLite, and SQL Server" },
        { icon: <GitPullRequest className="h-6 w-6" />, title: "Azure DevOps & Services", description: "Work items, repos, builds, PRs, and resource management" },
        { icon: <Mail className="h-6 w-6" />, title: "Outlook Integration", description: "Email and calendar integration with Microsoft 365" },
        { icon: <Folder className="h-6 w-6" />, title: "Filesystem & More", description: "Secure file operations, Memory MCP, Fetch MCP, and Claude Context" },
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-20 pb-20">
      <section className="py-20 bg-gradient-to-b from-purple-950/10 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <h1 className="text-5xl md:text-6xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Comprehensive Features
              </span>
            </h1>
            <p className="text-xl text-foreground/70">
              Everything you need to automate your workflow and boost productivity
            </p>
          </div>

          <Tabs defaultValue="ai" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-12">
              {featureCategories.map((category) => (
                <TabsTrigger key={category.id} value={category.id} className="flex items-center space-x-2">
                  {category.icon}
                  <span className="hidden sm:inline">{category.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {featureCategories.map((category) => (
              <TabsContent key={category.id} value={category.id}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.features.map((feature, index) => (
                    <Card key={index} className="group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30">
                      <CardContent className="p-6 space-y-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform duration-300">
                          {feature.icon}
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
                        <p className="text-foreground/60 text-sm">{feature.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Features;
